import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { getArticleLeadVideo } from "scmp-app/components/content/content-cover-video/helpers";
import { VideoPlayMode } from "scmp-app/components/scmp-youtube-video/enums";
import { useVideoPlayerControls } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-player/hooks";
import { SCMPYoutubeVideoProgressBar } from "scmp-app/components/scmp-youtube-video/scmp-youtube-video-progress-bar";
import type { VideoPlayerYouTubeEventCustomizedParameters } from "scmp-app/components/tracking/google-analytics-4/types";
import { getVideoYoutubeThumbnailUrl } from "scmp-app/components/video/helpers";
import type { contentVideoClickToPlayContent$key } from "scmp-app/queries/__generated__/contentVideoClickToPlayContent.graphql";

import { useCopyLink } from "./hooks";
import IconShareLink from "./icon-share-link.svg";
import IconSoundOff from "./icon-sound-off.svg";
import IconSoundOn from "./icon-sound-on.svg";
import {
  ButtonsContainer,
  LinkCopied,
  MuteButton,
  ProgressBar,
  ShareButton,
  StyledSCMPYoutubeVideo,
  VideoWrapper,
} from "./styles";

export type Props = {
  className?: string;
  ga4Position?: VideoPlayerYouTubeEventCustomizedParameters["position"];
  reference: contentVideoClickToPlayContent$key;
};

export const ContentVideoClickToPlay: FunctionComponent<Props> = ({
  className,
  ga4Position,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment contentVideoClickToPlayContent on Content {
        entityId
        urlAlias
        ... on Article {
          multimediaEmbed {
            json
          }
        }
      }
    `,
    reference,
  );

  const {
    handlePlaybackStarted,
    handleSeek,
    handleToggleMute,
    hasPlaybackStarted,
    isMuted,
    playerReference,
    progress,
  } = useVideoPlayerControls();

  const leadVideo = getArticleLeadVideo(content.multimediaEmbed?.json);

  const { handleCopyLink, isLinkCopied } = useCopyLink(content.urlAlias);

  if (!leadVideo) return null;

  const posterImageSource = first(getVideoYoutubeThumbnailUrl(leadVideo.youtubeId));

  return (
    <VideoWrapper className={className}>
      <StyledSCMPYoutubeVideo
        controls={false}
        enableAutoPlay={false}
        entityId={content.entityId}
        gaData={{
          ga4ArticleEntityId: content.entityId,
          ga4Position,
        }}
        imageSource={posterImageSource}
        index={0}
        initialVideoPlayMode={VideoPlayMode.Unset}
        isEnabledImaManager={false}
        isMuteVideo={false}
        onPlaybackStarted={handlePlaybackStarted}
        playerRef={playerReference}
        youtubeVideoId={leadVideo.youtubeId}
      />
      {hasPlaybackStarted && (
        <>
          <ButtonsContainer>
            <MuteButton
              aria-label={isMuted ? "Unmute" : "Mute"}
              onClick={event => {
                event.stopPropagation();
                handleToggleMute();
              }}
            >
              {isMuted ? <IconSoundOff /> : <IconSoundOn />}
            </MuteButton>
            <ShareButton
              aria-label="Copy link"
              onClick={event => {
                event.stopPropagation();
                handleCopyLink();
              }}
            >
              <IconShareLink />
              <LinkCopied $isVisible={isLinkCopied}>Link Copied</LinkCopied>
            </ShareButton>
          </ButtonsContainer>
          <ProgressBar>
            <SCMPYoutubeVideoProgressBar
              bufferedFraction={progress.loadedFraction}
              currentTime={progress.currentTime}
              duration={progress.duration}
              onSeek={handleSeek}
            />
          </ProgressBar>
        </>
      )}
    </VideoWrapper>
  );
};

ContentVideoClickToPlay.displayName = "ContentVideoClickToPlay";
